/**
 * Help (REDESIGN.md 7.5): picture first, readable in under a minute. Opened
 * from the "?" button on the table and from home. Footer: Learn to play (the
 * tutorial at /learn; not offered at an online table or inside the tutorial),
 * and About.
 */
import { useState } from 'react';
import { APP_VERSION, TAGLINE } from '../brand';
import { coarsePointer } from '../lib/motion';
import { navigate, useRoute } from '../lib/router';
import { Crest } from './Art';
import { FlatCard } from './Card';
import { IconChevron, IconToss } from './Icons';
import { Sheet } from './Sheet';

const W = 30;

function MiniGrid({ up, pair }: { up: (number | null)[]; pair?: number }) {
  return (
    <div className="htp-grid">
      {[0, 1, 2, 3].map((c) => (
        <div key={c} className={`htp-col${pair === c ? ' paired' : ''}`}>
          <FlatCard value={up[c] ?? null} width={W} peek={false} />
          <FlatCard value={up[c + 4] ?? null} width={W} peek={false} />
          {pair === c && <span className="pair-frame" />}
        </div>
      ))}
    </div>
  );
}

/** `onTour` is kept for callers but no longer rendered: Learn to play replaced the tour link. */
export function HowToPlayContent({ onLearn }: { onTour?: () => void; onLearn?: () => void }) {
  const [keys, setKeys] = useState(false);
  const [about, setAbout] = useState(false);
  const pointer = !coarsePointer();
  return (
    <div className="htp">
      <section className="htp-step">
        <div className="htp-pic htp-score" aria-hidden="true">
          <span className="htp-sc-row">
            <i>4</i>
            <i>0</i>
            <i>7</i>
            <b className="num">11</b>
          </span>
          <span className="htp-sc-row win">
            <i>2</i>
            <i>1</i>
            <i>3</i>
            <b className="num">6</b>
          </span>
        </div>
        <p>
          <strong>Lowest score after 9 holes wins.</strong>
        </p>
      </section>

      <section className="htp-step">
        <div className="htp-pic" aria-hidden="true">
          <MiniGrid up={[null, 4, null, null, null, null, 9, null]} />
        </div>
        <p>You get eight cards. Flip any two to start.</p>
      </section>

      <section className="htp-step htp-turn">
        <div className="htp-pic htp-flow" aria-hidden="true">
          <span className="htp-move">
            <span className="htp-pair">
              <FlatCard value={null} width={W} peek={false} />
              <FlatCard value={2} width={W} peek={false} />
            </span>
            <em>Draw</em>
          </span>
          <span className="htp-arrow">→</span>
          <span className="htp-move">
            <span className="htp-pair htp-anim-swap">
              <FlatCard value={2} width={W} peek={false} />
            </span>
            <em>Swap</em>
          </span>
          <span className="htp-or">or</span>
          <span className="htp-move">
            <span className="htp-pair">
              <span className="htp-toss">
                <IconToss size={20} />
              </span>
              <span className="htp-anim-flip">
                <FlatCard value={null} width={W} peek={false} />
              </span>
            </span>
            <em>Toss + flip</em>
          </span>
        </div>
        <p>Each turn: draw from the deck or the discard pile, then swap it into any slot. A deck card can be tossed instead; then flip one of yours.</p>
      </section>

      <section className="htp-step">
        <div className="htp-pic" aria-hidden="true">
          <MiniGrid up={[7, 3, null, 10, 7, null, 1, null]} pair={0} />
        </div>
        <p>
          A matching column scores 0. −5 pairs score −10. Two or more matching columns of the same card earn an extra −10, −15 or −20.
        </p>
      </section>

      <section className="htp-step">
        <div className="htp-pic htp-flagpic" aria-hidden="true">
          <MiniGrid up={[2, 5, 0, 8, 3, 5, 1, 4]} />
        </div>
        <p>The hole ends when someone flips their last card. Everyone else gets one last shot.</p>
      </section>

      <section className="htp-step htp-values">
        <div className="htp-strip" aria-label="Card values from −5 to 12">
          {[-5, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((v) => (
            <FlatCard key={v} value={v} width={44} />
          ))}
        </div>
      </section>

      {pointer && (
        <section className="htp-more">
          <button type="button" className="htp-toggle" aria-expanded={keys} onClick={() => setKeys((k) => !k)}>
            Keyboard <IconChevron open={keys} />
          </button>
          {keys && (
            <ul className="htp-keys">
              <li>
                <kbd>D</kbd> deck
              </li>
              <li>
                <kbd>F</kbd> discard pile
              </li>
              <li>
                <kbd>X</kbd> toss
              </li>
              <li>
                <kbd>1</kbd>–<kbd>8</kbd> your cards
              </li>
              <li>
                <kbd>Enter</kbd> confirm
              </li>
              <li>
                <kbd>P</kbd> keep hidden
              </li>
              <li>
                <kbd>H</kbd> hint (vs computer)
              </li>
              <li>arrows move between cards</li>
            </ul>
          )}
        </section>
      )}

      <div className="htp-foot">
        {onLearn && (
          <button type="button" className="btn btn-ink btn-sm" onClick={onLearn}>
            Learn to play
          </button>
        )}
        <button type="button" className="link-btn ink" aria-expanded={about} onClick={() => setAbout((x) => !x)}>
          About
        </button>
      </div>
      {about && (
        <div className="htp-about">
          <Crest width={130} motto />
          <p className="htp-tag">{TAGLINE}</p>
          <p className="htp-ver">Version {APP_VERSION}</p>
        </div>
      )}
    </div>
  );
}

export function HowToPlay({ open, onClose }: { open: boolean; onClose: () => void; onTour?: () => void }) {
  const route = useRoute();
  // Leaving an online table for a lesson would abandon the seat.
  const learn = route.name !== 'room' && route.name !== 'learn';
  return (
    <Sheet open={open} onClose={onClose} title="How to play" labelledBy="htp-title" className="htp-sheet">
      <HowToPlayContent
        {...(learn
          ? {
              onLearn: () => {
                onClose();
                navigate({ name: 'learn' });
              },
            }
          : {})}
      />
    </Sheet>
  );
}
